import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AuthLoadingScreen from "../components/features/AuthLoadingScreen";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { getInsightsSsoUrl } from "../shared/insightsSso";
import { getLeadB2bSsoUrl } from "../shared/leadB2bSso";
import { getLeadflowSsoUrl } from "../shared/leadflowSso";
import { getSiteBuilderSsoUrl } from "../shared/siteBuilderSso";
import { getVirtusCrmSsoUrl } from "../shared/virtusCrmSso";
import { clearPostLoginPath } from "../shared/postLoginRedirect";

/**
 * Ponte de SSO do menu de aplicações (AppLauncher do @virtus-automation/platform-ui).
 *
 * As outras aplicações não têm a sessão do SMM, então o menu delas aponta pra cá:
 * `/launch/<app>` roda dentro do ProtectedRoute (sem sessão, cai no login), gera a URL de SSO da
 * aplicação com a sessão atual e redireciona nesta mesma aba.
 */
const LaunchApp: React.FC = () => {
  const { app } = useParams<{ app: string }>();
  const { activeContext } = useActiveContext();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Chegou ao destino guardado antes do login: não redireciona para cá de novo num próximo login.
    clearPostLoginPath();
    let cancelled = false;

    const resolveUrl = async (): Promise<string | null> => {
      switch (app) {
        case "leadflow":
          return getLeadflowSsoUrl();
        case "whatsapp":
          return getVirtusCrmSsoUrl(activeContext);
        case "lead-b2b":
          return getLeadB2bSsoUrl();
        case "site-builder":
          return getSiteBuilderSsoUrl();
        case "insights":
          return getInsightsSsoUrl();
        default:
          return null;
      }
    };

    resolveUrl()
      .then((url) => {
        if (cancelled) return;
        if (!url) {
          setError(t("Aplicação não encontrada."));
          return;
        }
        window.location.replace(url);
      })
      .catch(() => {
        if (!cancelled)
          setError(t("Não foi possível abrir a aplicação. Tente novamente."));
      });

    return () => {
      cancelled = true;
    };
    // activeContext só é lido no momento do redirecionamento (CRM); não precisa refazer ao mudar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-50 p-6 text-center">
        <p className="text-sm font-semibold text-gray-700">{error}</p>
        <Link
          to="/"
          className="text-sm font-semibold text-purple-700 hover:underline"
        >
          {t("Voltar ao Social Manager")}
        </Link>
      </div>
    );
  }

  return <AuthLoadingScreen message={t("Abrindo a aplicação...")} />;
};

export default LaunchApp;
